import DPO from 'dpo8core';
import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import classnames from 'classnames';
import LocalizedText from '@LocalizedText';
import useIntlMessage from '@app/hooks/use-intl-message';
import AutoValidateForm from '@components/auto-validate/AutoValidateForm';
import './AuthorizeCodes.scss';

const AuthorizeCodesForm = ({ setFormValid, setFormData }) => {
  const [
    fullNameInputAriaLabel,
    companyNameInputAriaLabel,
    emailInputAriaLabel,
    transparencyBrandsInputAriaLabel,
    shippingAddressInputAriaLabel,
  ] = useIntlMessage(
    'qau-full-name-input-aria-label',
    'qau-company-name-input-aria-label',
    'qau-email-input-aria-label',
    'qau-transparency-brands-input-aria-label',
    'qau-shipping-address-input-aria-label',
  );
  const validateText = value => !value.trim().length;
  const validateEmail = email => DPO.project.Validator.email(email);
  const validateBrandId = brandId => !/^([a-zA-Z0-9]+)$/.test(brandId);
  const validateCheck = isChecked => !isChecked;

  const onFormValidated = (errors, values) => {
    setFormValid(!Object.keys(errors).length);
    setFormData(values);
  };

  return (
    <div id="authorize-codes-form">
      <Formik
        initialValues={{
          email: '',
          fullname: '',
          company_name: '',
          brand_id: '',
          shipping_address: '',
          confirm: false,
        }}
      >
        {({ errors, touched }) => (
          <Form>
            <div className="form-column left">
              <label htmlFor="authorize-full-name" className="required">
                <LocalizedText id="qau-full-name" tagName="span" />
                <Field
                  type="text"
                  name="fullname"
                  id="authorize-full-name"
                  aria-label={fullNameInputAriaLabel}
                  validate={validateText}
                  maxLength="100"
                  className={classnames({ invalid: errors.fullname && touched.fullname })}
                />
              </label>

              <label htmlFor="authorize-company-name" className="required">
                <LocalizedText id="qau-company-name" tagName="span" />
                <Field
                  type="text"
                  name="company_name"
                  id="authorize-company-name"
                  aria-label={companyNameInputAriaLabel}
                  validate={validateText}
                  maxLength="100"
                  className={classnames({ invalid: errors.company_name && touched.company_name })}
                />
              </label>

              <label htmlFor="authorize-email" className="required">
                <LocalizedText id="qau-email" tagName="span" />
                <Field
                  type="text"
                  name="email"
                  id="authorize-email"
                  aria-label={emailInputAriaLabel}
                  validate={validateEmail}
                  maxLength="100"
                  className={classnames({ invalid: errors.email && touched.email })}
                />
              </label>
            </div>
            <div className="form-column right">
              <label htmlFor="authorize-brand-id" className="required">
                <LocalizedText id="qau-brand-id" tagName="span" />
                <Field
                  type="text"
                  name="brand_id"
                  id="authorize-brand-id"
                  aria-label={transparencyBrandsInputAriaLabel}
                  validate={validateBrandId}
                  maxLength="64"
                  className={classnames({ invalid: errors.brand_id && touched.brand_id })}
                />
              </label>
              <label htmlFor="authorize-shipping-address">
                <LocalizedText id="qau-shipping-address" tagName="span" />
                <Field
                  type="text"
                  name="shipping_address"
                  id="authorize-shipping-address"
                  aria-label={shippingAddressInputAriaLabel}
                  maxLength="100"
                />
              </label>
              <label htmlFor="authorize-confirm" className="required">
                <Field
                  type="checkbox"
                  name="confirm"
                  id="authorize-confirm"
                  validate={validateCheck}
                  className={classnames({ invalid: errors.confirm && touched.confirm })}
                />
                <LocalizedText id="qau-authorization-check" tagName="span" />
              </label>
            </div>
            <AutoValidateForm onValidated={onFormValidated} />
          </Form>
        )}
      </Formik>
    </div>
  );
};

AuthorizeCodesForm.propTypes = {
  setFormValid: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
};

export default AuthorizeCodesForm;
